import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import {
  clampDashboardSidebarWidth,
  DASHBOARD_SIDEBAR_MAX_WIDTH_PX,
  DASHBOARD_SIDEBAR_MIN_WIDTH_PX,
} from '../../utils/navigation/dashboardSidebarLayout';

const CLICK_MOVE_THRESHOLD_PX = 4;

const Handle = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 10px;
  z-index: 1205;
  cursor: col-resize;
  touch-action: none;
  user-select: none;
`;

function SidebarResizeHandle({ widthPx, onWidthChange, onCollapse }) {
  const widthRef = useRef(widthPx);
  const onWidthChangeRef = useRef(onWidthChange);
  const onCollapseRef = useRef(onCollapse);

  useEffect(() => {
    widthRef.current = widthPx;
  }, [widthPx]);

  useEffect(() => {
    onWidthChangeRef.current = onWidthChange;
  }, [onWidthChange]);

  useEffect(() => {
    onCollapseRef.current = onCollapse;
  }, [onCollapse]);

  const onMouseDown = (event) => {
    if (event.button !== 0) return;
    event.preventDefault();
    const handleEl = event.currentTarget;
    const startX = event.clientX;
    const startWidth = widthRef.current;
    let moved = false;
    handleEl.dataset.active = 'true';

    let onMouseMove;
    let onMouseUp;

    const stopListening = () => {
      handleEl.dataset.active = 'false';
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };

    onMouseMove = (moveEvent) => {
      const delta = moveEvent.clientX - startX;
      if (Math.abs(delta) <= CLICK_MOVE_THRESHOLD_PX) return;
      moved = true;
      const nextWidth = startWidth + delta;
      // Dragging past the minimum snaps closed (Linear-style).
      if (nextWidth < DASHBOARD_SIDEBAR_MIN_WIDTH_PX) {
        stopListening();
        onCollapseRef.current();
        return;
      }
      onWidthChangeRef.current(clampDashboardSidebarWidth(nextWidth));
    };

    onMouseUp = (upEvent) => {
      stopListening();
      const delta = upEvent.clientX - startX;
      const nextWidth = startWidth + delta;
      if (!moved && Math.abs(delta) <= CLICK_MOVE_THRESHOLD_PX) {
        onCollapseRef.current();
        return;
      }
      if (moved && nextWidth < DASHBOARD_SIDEBAR_MIN_WIDTH_PX) {
        onCollapseRef.current();
      }
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  return (
    <Handle
      data-sidebar-resize-handle
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize navigation sidebar"
      title="Drag to resize / Click to collapse"
      aria-valuemin={DASHBOARD_SIDEBAR_MIN_WIDTH_PX}
      aria-valuemax={DASHBOARD_SIDEBAR_MAX_WIDTH_PX}
      aria-valuenow={widthPx}
      onMouseDown={onMouseDown}
    />
  );
}

export default SidebarResizeHandle;
